import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Globe, Search, Shield, MapPin, Eye, AlertTriangle,
  ChevronDown, ChevronUp, RefreshCw, FileText, Zap, Target,
  GitCompare, ArrowUp, ArrowDown, Minus, Copy, Check, Download,
  Send, ExternalLink
} from "lucide-react";

const PLATFORM_ICONS = {
  google: "G", bing: "B", youtube: "YT", reddit: "R",
  tiktok: "TK", instagram: "IG", deviantart: "DA",
  discord: "DC", twitter: "X", pinterest: "PIN",
  facebook: "FB", linkedin: "LI", twitch: "TW",
  threads: "TH", bluesky: "BS", quora: "Q",
  medium: "M", vimeo: "VM", dailymotion: "DM",
  rumble: "RU", knowyourmeme: "KYM", fandom: "FAN",
  archiveorg: "ARC", patreon: "PAT", misc_web: "WEB",
};

const PLATFORM_COLORS = {
  google: "text-blue-400 border-blue-500/30",
  bing: "text-teal-400 border-teal-500/30",
  youtube: "text-red-400 border-red-500/30",
  reddit: "text-orange-400 border-orange-500/30",
  tiktok: "text-pink-400 border-pink-500/30",
  instagram: "text-purple-400 border-purple-500/30",
  deviantart: "text-green-400 border-green-500/30",
  discord: "text-indigo-400 border-indigo-500/30",
  twitter: "text-sky-400 border-sky-500/30",
  pinterest: "text-red-300 border-red-400/30",
  facebook: "text-blue-300 border-blue-400/30",
  linkedin: "text-blue-500 border-blue-600/30",
  twitch: "text-violet-400 border-violet-500/30",
  threads: "text-zinc-300 border-zinc-400/30",
  bluesky: "text-sky-300 border-sky-400/30",
  quora: "text-red-400 border-red-500/30",
  medium: "text-emerald-400 border-emerald-500/30",
  vimeo: "text-cyan-300 border-cyan-400/30",
  dailymotion: "text-blue-200 border-blue-300/30",
  rumble: "text-green-300 border-green-400/30",
  knowyourmeme: "text-yellow-300 border-yellow-400/30",
  fandom: "text-lime-400 border-lime-500/30",
  archiveorg: "text-amber-400 border-amber-500/30",
  patreon: "text-orange-300 border-orange-400/30",
  misc_web: "text-zinc-400 border-zinc-500/30",
};

export const GlobalScan = ({ channels, API }) => {
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [pastReports, setPastReports] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [scanDiff, setScanDiff] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [copiedReport, setCopiedReport] = useState(false);
  const pollRef = useRef(null);

  // Load past reports when channel changes
  useEffect(() => {
    if (selectedChannel) {
      loadPastReports(selectedChannel.id);
      checkExistingScan(selectedChannel.id);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedChannel]);

  const loadPastReports = async (channelId) => {
    try {
      const res = await axios.get(`${API}/global-scan/reports/${channelId}`);
      setPastReports(res.data.reports || []);
    } catch { setPastReports([]); }
  };

  const checkExistingScan = async (channelId) => {
    try {
      const res = await axios.get(`${API}/global-scan/status/${channelId}`);
      if (res.data.status === "running") {
        setScanStatus(res.data);
        setScanning(true);
        startPolling(channelId);
      } else if (res.data.status === "complete") {
        loadReport(channelId);
      }
    } catch {}
  };

  const startScan = async () => {
    if (!selectedChannel) return;
    setScanning(true);
    setReport(null);
    setScanStatus({ progress: 0, progress_message: "Initializing..." });
    try {
      await axios.post(`${API}/global-scan/start/${selectedChannel.id}`);
      toast.success("GLOBAL SCAN INITIATED", { description: "Searching all platforms..." });
      startPolling(selectedChannel.id);
    } catch (e) {
      toast.error("Scan failed to start", { description: e.response?.data?.detail || e.message });
      setScanning(false);
    }
  };

  const startPolling = (channelId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/global-scan/status/${channelId}`);
        setScanStatus(res.data);
        if (res.data.status === "complete") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setScanning(false);
          loadReport(channelId);
          loadPastReports(channelId);
          toast.success("GLOBAL SCAN COMPLETE");
        } else if (res.data.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setScanning(false);
          toast.error("Scan failed", { description: res.data.progress_message });
        }
      } catch {}
    }, 3000);
  };

  const loadReport = async (channelId) => {
    try {
      const res = await axios.get(`${API}/global-scan/report/${channelId}`);
      setReport(res.data);
    } catch {}
  };

  const runDiff = async () => {
    if (!selectedChannel) return;
    setLoadingDiff(true);
    setScanDiff(null);
    try {
      const res = await axios.post(`${API}/global-scan/diff/${selectedChannel.id}`);
      setScanDiff(res.data);
      toast.success("SCAN DIFF GENERATED");
    } catch (e) {
      toast.error("Diff failed", { description: e.response?.data?.detail || e.message });
    }
    setLoadingDiff(false);
  };

  const getDeltaIcon = (d) => {
    if (d > 0) return <ArrowUp className="w-3 h-3 text-green-400 inline" />;
    if (d < 0) return <ArrowDown className="w-3 h-3 text-red-400 inline" />;
    return <Minus className="w-3 h-3 text-zinc-600 inline" />;
  };

  const getDeltaColor = (d) => d > 0 ? "text-green-400" : d < 0 ? "text-red-400" : "text-zinc-600";

  const toggleSection = (key) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const getScoreColor = (s) =>
    s >= 75 ? "text-green-400" : s >= 50 ? "text-cyan-400" : s >= 25 ? "text-yellow-400" : "text-red-400";

  const getScoreBg = (s) =>
    s >= 75 ? "border-green-500" : s >= 50 ? "border-cyan-500" : s >= 25 ? "border-yellow-500" : "border-red-500";

  return (
    <div data-testid="globalscan-view" className="max-w-5xl">
      <h2 className="font-display font-bold text-xl tracking-wider text-white mb-1">
        GLOBAL SCAN
      </h2>
      <p className="text-zinc-500 text-sm mb-6">
        Search every corner of the internet for your target. Find where they're visible, where they're hidden, and what keywords lead to them.
      </p>

      {/* Channel Selector */}
      <div className="widget mb-6">
        <div className="widget-header">
          <span className="widget-title">
            <Target className="w-4 h-4 inline mr-2 text-cyan-400" />
            SELECT TARGET
          </span>
        </div>
        <div className="widget-content">
          <div className="flex flex-wrap gap-2 mb-4">
            {channels.map((ch) => (
              <button
                key={ch.id}
                onClick={() => { setSelectedChannel(ch); setReport(null); setScanStatus(null); setScanning(false); }}
                data-testid={`globalscan-channel-${ch.id}`}
                className={`px-4 py-2 text-xs border transition-all flex items-center gap-2 ${
                  selectedChannel?.id === ch.id
                    ? "border-cyan-500 bg-cyan-500/20 text-cyan-300"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {ch.handle || ch.name}
              </button>
            ))}
          </div>

          {selectedChannel && !scanning && (
            <button
              onClick={startScan}
              data-testid="start-global-scan-btn"
              className="px-6 py-2.5 text-xs font-bold bg-gradient-to-r from-red-600 to-orange-600 text-white hover:from-red-500 hover:to-orange-500 transition-all flex items-center gap-2"
            >
              <Globe className="w-4 h-4" /> LAUNCH GLOBAL SCAN
            </button>
          )}
        </div>
      </div>

      {/* Scan Progress */}
      {scanning && scanStatus && (
        <div className="widget mb-6" data-testid="scan-progress">
          <div className="widget-header">
            <span className="widget-title">
              <Loader2 className="w-4 h-4 inline mr-2 animate-spin text-yellow-400" />
              SCANNING...
            </span>
            <span className="text-xs text-yellow-400">{scanStatus.progress || 0}%</span>
          </div>
          <div className="widget-content">
            <div className="w-full h-2 bg-zinc-800 mb-3">
              <div
                className="h-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-500"
                style={{ width: `${scanStatus.progress || 0}%` }}
              />
            </div>
            <p className="text-xs text-zinc-400">{scanStatus.progress_message || "Working..."}</p>
          </div>
        </div>
      )}

      {/* Report Display */}
      {report && (
        <>
          {/* Visibility Score */}
          <div className="widget mb-6" data-testid="visibility-score">
            <div className="widget-header">
              <span className="widget-title">
                <Eye className="w-4 h-4 inline mr-2 text-green-400" />
                VISIBILITY SCORE
              </span>
              <span className="text-[10px] text-zinc-500">
                {report.stats?.total_searches || 0} searches | {report.stats?.total_results || 0} results
              </span>
            </div>
            <div className="widget-content">
              <div className="flex items-center gap-6 mb-5">
                <div className={`w-24 h-24 flex items-center justify-center border-4 ${getScoreBg(report.visibility_score?.total || 0)}`}>
                  <span className={`text-3xl font-bold ${getScoreColor(report.visibility_score?.total || 0)}`}>
                    {report.visibility_score?.total || 0}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-white mb-1">
                    Global Visibility: <span className={`font-bold ${getScoreColor(report.visibility_score?.total || 0)}`}>
                      {report.visibility_score?.total || 0}/100
                    </span>
                  </p>
                  <p className="text-xs text-zinc-500">
                    {report.visibility_score?.platforms_found || 0} platforms | {report.visibility_score?.total_relevant_results || 0} relevant results | {report.visibility_score?.keywords_matched || 0} keyword matches
                  </p>
                  <p className="text-xs text-zinc-600 mt-1">
                    Regions: {report.regions_checked?.join(", ")}
                    {report.stats?.blocked_keywords > 0 && (
                      <span className="text-orange-400 ml-2">| {report.stats.blocked_keywords} blocked keywords</span>
                    )}
                    {report.stats?.restrictions_bypassed > 0 && (
                      <span className="text-green-400 ml-2">| {report.stats.restrictions_bypassed} bypassed</span>
                    )}
                    {report.stats?.restrictions_unbreakable > 0 && (
                      <span className="text-red-400 ml-2">| {report.stats.restrictions_unbreakable} unbreakable</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Sub-scores */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { label: "PLATFORM", key: "platform_coverage" },
                  { label: "VOLUME", key: "result_volume" },
                  { label: "CONFIDENCE", key: "confidence_quality" },
                  { label: "KEYWORDS", key: "keyword_diversity" },
                  { label: "REGIONAL", key: "regional_presence" },
                ].map(({ label, key }) => (
                  <div key={key} className="p-2 bg-zinc-900 border border-zinc-800 text-center">
                    <p className="text-[10px] text-zinc-500">{label}</p>
                    <p className={`text-lg font-bold ${getScoreColor(report.visibility_score?.[key] || 0)}`}>
                      {report.visibility_score?.[key] || 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Platform Results */}
          <div className="widget mb-6">
            <div className="widget-header">
              <span className="widget-title">
                <Search className="w-4 h-4 inline mr-2 text-blue-400" />
                PLATFORM RESULTS
              </span>
            </div>
            <div className="widget-content space-y-3">
              {Object.entries(report.platform_results || {}).map(([platform, results]) => {
                const relevant = results.filter(r => r.relevance?.relevant);
                const color = PLATFORM_COLORS[platform] || "text-zinc-400 border-zinc-500/30";
                const isOpen = expandedSections[`plat_${platform}`];
                return (
                  <div key={platform} className="border border-zinc-800">
                    <button
                      onClick={() => toggleSection(`plat_${platform}`)}
                      className="w-full flex items-center justify-between p-3 hover:bg-zinc-900/50 transition-colors"
                      data-testid={`platform-${platform}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold border px-2 py-0.5 ${color}`}>
                          {PLATFORM_ICONS[platform] || platform.toUpperCase()}
                        </span>
                        <span className="text-xs text-white">{platform.toUpperCase()}</span>
                        <span className="text-[10px] text-zinc-500">
                          {relevant.length} relevant / {results.length} total
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {relevant.length > 0 && <span className="w-2 h-2 bg-green-400 rounded-full" />}
                        {isOpen ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="p-3 pt-0 space-y-1.5">
                        {results.slice(0, 10).map((r, i) => (
                          <div key={i} className={`p-2 text-xs border ${r.relevance?.relevant ? "border-zinc-700 bg-zinc-900/80" : "border-zinc-800/50 opacity-50"}`}>
                            <div className="flex items-start gap-2">
                              {r.relevance?.relevant && (
                                <span className={`shrink-0 text-[9px] px-1 border ${
                                  r.relevance.confidence === "high" ? "text-green-400 border-green-500/30" :
                                  r.relevance.confidence === "medium" ? "text-yellow-400 border-yellow-500/30" :
                                  "text-zinc-500 border-zinc-600/30"
                                }`}>{r.relevance.confidence?.toUpperCase()}</span>
                              )}
                              <div className="min-w-0 flex-1">
                                <a href={r.url} target="_blank" rel="noreferrer"
                                  className="text-cyan-400 hover:text-cyan-300 truncate block">{r.title || r.url}</a>
                                <p className="text-zinc-600 truncate mt-0.5">{r.snippet}</p>
                              </div>
                              {r.restriction && (
                                <span className="shrink-0 text-[9px] text-red-400 border border-red-500/30 px-1">
                                  {r.restriction}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Keyword Matches */}
          <div className="widget mb-6">
            <div className="widget-header">
              <span className="widget-title">
                <Zap className="w-4 h-4 inline mr-2 text-yellow-400" />
                KEYWORD INTELLIGENCE
              </span>
              <span className="text-xs text-zinc-500">{Object.keys(report.keyword_matches || {}).length} keywords matched</span>
            </div>
            <div className="widget-content">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(report.keyword_matches || {}).sort((a, b) => b[1].length - a[1].length).slice(0, 16).map(([kw, matches]) => (
                  <div key={kw} className="flex items-center justify-between p-2 bg-zinc-900 border border-zinc-800">
                    <span className="text-xs text-zinc-300 truncate mr-2">{kw}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs font-bold text-cyan-400">{matches.length}</span>
                      <span className="text-[9px] text-zinc-600">hits</span>
                    </div>
                  </div>
                ))}
              </div>
              {report.keywords_used && (
                <button
                  onClick={() => toggleSection("all_keywords")}
                  className="flex items-center gap-1 text-[10px] text-zinc-600 hover:text-zinc-400 mt-3"
                >
                  {expandedSections.all_keywords ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  All {report.keyword_count || 0} keywords searched
                </button>
              )}
              {expandedSections.all_keywords && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(report.keywords_used || []).map((kw, i) => (
                    <span key={i} className={`text-[10px] px-2 py-0.5 border ${
                      report.keyword_matches?.[kw] ? "text-green-400 border-green-500/30" : "text-zinc-600 border-zinc-800"
                    }`}>{kw}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Blocked Keywords — Should Have Found */}
          {report.keyword_intelligence?.blocked?.length > 0 && (
            <div className="widget mb-6" data-testid="blocked-keywords">
              <div className="widget-header">
                <span className="widget-title">
                  <AlertTriangle className="w-4 h-4 inline mr-2 text-orange-400" />
                  BLOCKED KEYWORDS — SHOULD HAVE FOUND TARGET
                </span>
                <span className="text-xs text-orange-400 border border-orange-500/30 px-2 py-0.5">
                  {report.keyword_intelligence.blocked.length} BLOCKED
                </span>
              </div>
              <div className="widget-content space-y-2">
                <p className="text-[10px] text-zinc-500 mb-2">
                  These keywords are directly related to the target but returned ZERO results — potential shadow-ban, de-indexing, or algorithmic suppression.
                </p>
                {report.keyword_intelligence.blocked.map((b, i) => (
                  <div key={i} className="flex items-start gap-3 p-2.5 bg-orange-500/5 border border-orange-500/20">
                    <span className="text-orange-400 font-bold text-xs shrink-0 mt-0.5">!</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-orange-300 font-semibold">{b.keyword}</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{b.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bypass Audit */}
          {report.bypass_audit?.length > 0 && (
            <div className="widget mb-6" data-testid="bypass-audit">
              <div className="widget-header">
                <span className="widget-title">
                  <Shield className="w-4 h-4 inline mr-2 text-amber-400" />
                  RESTRICTION BYPASS AUDIT
                </span>
                <span className="flex items-center gap-2">
                  {report.bypass_summary?.bypassed > 0 && (
                    <span className="text-xs text-green-400 border border-green-500/30 px-2 py-0.5">
                      {report.bypass_summary.bypassed} BYPASSED
                    </span>
                  )}
                  {report.bypass_summary?.still_blocked > 0 && (
                    <span className="text-xs text-red-400 border border-red-500/30 px-2 py-0.5">
                      {report.bypass_summary.still_blocked} UNBREAKABLE
                    </span>
                  )}
                </span>
              </div>
              <div className="widget-content space-y-2">
                {report.bypass_audit.map((a, i) => {
                  const isOpen = expandedSections[`bypass_${i}`];
                  const successAttempts = (a.bypass_attempts || []).filter(att => att.success);
                  const failAttempts = (a.bypass_attempts || []).filter(att => !att.success);
                  return (
                    <div key={i} className={`border ${a.bypassed ? "border-green-500/20" : "border-red-500/20"}`}>
                      <button
                        onClick={() => toggleSection(`bypass_${i}`)}
                        className="w-full flex items-center justify-between p-3 hover:bg-zinc-900/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 border ${
                            a.bypassed ? "text-green-400 border-green-500/30 bg-green-500/10" : "text-red-400 border-red-500/30 bg-red-500/10"
                          }`}>{a.bypassed ? "BYPASSED" : "BLOCKED"}</span>
                          <span className="text-xs text-zinc-300 truncate">{a.original_keyword}</span>
                          <span className="text-[9px] text-zinc-600">({a.restriction_type})</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[9px] text-zinc-500">
                            {(a.bypass_attempts || []).length} attempts
                          </span>
                          {isOpen ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="p-3 pt-0 space-y-1.5">
                          {a.bypassed && a.bypass_method && (
                            <div className="p-2 bg-green-500/10 border border-green-500/20 text-xs">
                              <span className="text-green-400 font-semibold">Bypass method: </span>
                              <span className="text-green-300">{a.bypass_method}</span>
                              {a.found_content && <p className="text-zinc-400 mt-1">Found: {a.found_content}</p>}
                            </div>
                          )}
                          {a.reason_expected && (
                            <p className="text-[10px] text-zinc-500 italic">Expected: {a.reason_expected}</p>
                          )}
                          {(a.bypass_attempts || []).map((att, j) => (
                            <div key={j} className={`flex items-center gap-2 p-1.5 text-[10px] ${att.success ? "text-green-400" : "text-zinc-600"}`}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${att.success ? "bg-green-400" : "bg-red-400"}`} />
                              <span className="truncate">{att.method}</span>
                              {att.success && att.result_url && (
                                <a href={att.result_url} target="_blank" rel="noreferrer" className="text-cyan-500 truncate hover:underline ml-auto">
                                  {att.result_title || att.result_url}
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Restricted Results */}
          {report.restricted_results?.length > 0 && (
            <div className="widget mb-6">
              <div className="widget-header">
                <span className="widget-title">
                  <Shield className="w-4 h-4 inline mr-2 text-red-400" />
                  RESTRICTED / FILTERED CONTENT
                </span>
                <span className="text-xs text-red-400 border border-red-500/30 px-2 py-0.5">
                  {report.restricted_results.length} FLAGGED
                </span>
              </div>
              <div className="widget-content space-y-2">
                {report.restricted_results.slice(0, 12).map((r, i) => (
                  <div key={i} className="flex items-start gap-3 p-2 bg-red-500/5 border border-red-500/20">
                    <AlertTriangle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <a href={r.url} target="_blank" rel="noreferrer"
                        className="text-xs text-red-300 hover:text-red-200 truncate block">{r.title || r.url}</a>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{r.restriction} — {r.platform}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Geo-Block Matrix */}
          {report.geo_block_matrix?.length > 0 && (
            <div className="widget mb-6" data-testid="geo-block-matrix">
              <div className="widget-header">
                <span className="widget-title">
                  <Globe className="w-4 h-4 inline mr-2 text-amber-400" />
                  GEO-RESTRICTION MAP
                </span>
                <span className="text-xs text-zinc-500">
                  {report.stats?.geo_blocks_detected || 0} geo-blocks | {report.stats?.regions_probed || 0} regions
                </span>
              </div>
              <div className="widget-content space-y-3">
                <p className="text-[10px] text-zinc-500 mb-2">
                  Each restricted keyword tested across all regions. Shows where content is visible vs blocked.
                </p>
                {report.geo_block_matrix.map((g, i) => {
                  const isOpen = expandedSections[`geo_${i}`];
                  return (
                    <div key={i} className="border border-zinc-800">
                      <button
                        onClick={() => toggleSection(`geo_${i}`)}
                        className="w-full flex items-center justify-between p-3 hover:bg-zinc-900/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-zinc-300 truncate font-medium">{g.keyword}</span>
                          <span className="text-[9px] text-zinc-600">({g.restriction})</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[9px] text-green-400">{g.visible_in?.length || 0} visible</span>
                          <span className="text-[9px] text-red-400">{g.blocked_in?.length || 0} blocked</span>
                          {isOpen ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="p-3 pt-0">
                          <div className="grid grid-cols-2 gap-1">
                            {Object.entries(g.regions || {}).map(([code, rd]) => (
                              <div key={code} className={`flex items-center justify-between p-1.5 text-[10px] ${
                                rd.found ? "bg-green-500/5 border border-green-500/10" : "bg-red-500/5 border border-red-500/10"
                              }`}>
                                <span className={rd.found ? "text-green-300" : "text-red-300"}>{rd.label}</span>
                                <span className={`font-bold ${rd.found ? "text-green-400" : "text-red-400"}`}>
                                  {rd.found ? `${rd.result_count} found` : "BLOCKED"}
                                </span>
                              </div>
                            ))}
                          </div>
                          {g.visible_in?.length > 0 && g.blocked_in?.length > 0 && (
                            <p className="text-[10px] text-amber-400 mt-2">
                              Geo-block detected: Visible in {g.visible_in.join(", ")} but blocked in {g.blocked_in.join(", ")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Regional Differences */}
          {report.regional_differences && Object.keys(report.regional_differences).length > 0 && (
            <div className="widget mb-6">
              <div className="widget-header">
                <span className="widget-title">
                  <MapPin className="w-4 h-4 inline mr-2 text-purple-400" />
                  REGIONAL VISIBILITY ({Object.keys(report.regional_differences).length} regions)
                </span>
              </div>
              <div className="widget-content">
                {/* Tier 1 - Western Markets */}
                {(() => {
                  const t1 = Object.entries(report.regional_differences).filter(([,d]) => d.tier === 1);
                  const t2 = Object.entries(report.regional_differences).filter(([,d]) => d.tier === 2);
                  return (
                    <>
                      {t1.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] text-zinc-500 mb-2 font-semibold">TIER 1 — WESTERN MARKETS</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {t1.map(([code, data]) => (
                              <div key={code} className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                                <p className="text-xs text-zinc-500 mb-1">{data.label || code}</p>
                                <p className={`text-lg font-bold ${data.relevant_count > 0 ? "text-green-400" : "text-red-400"}`}>
                                  {data.relevant_count || 0}
                                </p>
                                <p className="text-[10px] text-zinc-600">{data.results_found || 0} results</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {t2.length > 0 && (
                        <div>
                          <p className="text-[10px] text-zinc-500 mb-2 font-semibold">TIER 2 — UNRESTRICTED / LIGHTER FILTERING</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {t2.map(([code, data]) => (
                              <div key={code} className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                                <p className="text-xs text-zinc-500 mb-1">{data.label || code}</p>
                                <p className={`text-lg font-bold ${data.relevant_count > 0 ? "text-green-400" : "text-red-400"}`}>
                                  {data.relevant_count || 0}
                                </p>
                                <p className="text-[10px] text-zinc-600">{data.results_found || 0} results</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Fallback for regions without tier */}
                      {t1.length === 0 && t2.length === 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(report.regional_differences).map(([code, data]) => (
                            <div key={code} className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                              <p className="text-xs text-zinc-500 mb-1">{data.label || code}</p>
                              <p className={`text-lg font-bold ${data.relevant_count > 0 ? "text-green-400" : "text-red-400"}`}>
                                {data.relevant_count || 0}
                              </p>
                              <p className="text-[10px] text-zinc-600">{data.results_found || 0} results</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* AI Report */}
          {report.ai_report && (() => {
            const fullReportText = `# GLOBAL VISIBILITY INTELLIGENCE REPORT\nTarget: ${report.handle} (${report.platform})\nScore: ${report.visibility_score?.total ?? "?"}/100\nScan ID: ${report.scan_id}\nCompleted: ${report.completed_at || ""}\n\n${report.ai_report}`;

            const copyText = async (text) => {
              try {
                await navigator.clipboard.writeText(text);
                return true;
              } catch {
                const ta = document.createElement("textarea");
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                let ok = false;
                try { ok = document.execCommand("copy"); } catch { ok = false; }
                document.body.removeChild(ta);
                return ok;
              }
            };

            const sendTo = async (service) => {
              // Instruction prepended so the target LLM knows what to do with the blob.
              const preface = `Please read and analyse the following Global Visibility Intelligence Report for ${report.handle} (${report.platform}). Summarise the key findings, highlight the most actionable recommendations, and flag anything suspicious.\n\n---\n\n`;
              const payload = preface + fullReportText;

              const MAX_URL_CHARS = 6500; // safe upper bound for URL length across browsers
              const encoded = encodeURIComponent(payload);
              const canPrefill = encoded.length <= MAX_URL_CHARS;

              // Always copy to clipboard as a safety net
              const copied = await copyText(payload);

              // URL strategy per service
              let url = "";
              let supportsPrefill = false;
              switch (service) {
                case "chatgpt":
                  url = canPrefill ? `https://chatgpt.com/?q=${encoded}` : "https://chatgpt.com/";
                  supportsPrefill = canPrefill;
                  break;
                case "claude":
                  url = canPrefill ? `https://claude.ai/new?q=${encoded}` : "https://claude.ai/new";
                  supportsPrefill = canPrefill;
                  break;
                case "grok":
                  url = canPrefill ? `https://grok.com/?q=${encoded}` : "https://grok.com/";
                  supportsPrefill = canPrefill;
                  break;
                case "perplexity":
                  url = canPrefill ? `https://www.perplexity.ai/?q=${encoded}` : "https://www.perplexity.ai/";
                  supportsPrefill = canPrefill;
                  break;
                case "gemini":
                  // Gemini has no public prefill param — open app, user pastes
                  url = "https://gemini.google.com/app";
                  supportsPrefill = false;
                  break;
                case "notebooklm":
                  // NotebookLM requires adding a source manually
                  url = "https://notebooklm.google.com/";
                  supportsPrefill = false;
                  break;
                default:
                  return;
              }

              window.open(url, "_blank", "noopener,noreferrer");

              const label = {
                chatgpt: "ChatGPT", claude: "Claude", grok: "Grok",
                perplexity: "Perplexity", gemini: "Gemini", notebooklm: "NotebookLM",
              }[service];

              if (supportsPrefill) {
                toast.success(`Sent to ${label} — the report is pre-loaded in a new tab`);
              } else if (copied) {
                toast.success(`${label} opened in new tab — report copied to clipboard, paste it into the prompt`);
              } else {
                toast.warning(`${label} opened. Click "Copy Report" first, then paste into the prompt.`);
              }
            };

            const llmButtons = [
              { id: "chatgpt", label: "ChatGPT", accent: "from-emerald-500/20 to-teal-500/10 border-emerald-500/40 text-emerald-300 hover:border-emerald-400" },
              { id: "claude", label: "Claude", accent: "from-orange-500/20 to-amber-500/10 border-orange-500/40 text-orange-300 hover:border-orange-400" },
              { id: "gemini", label: "Gemini", accent: "from-blue-500/20 to-indigo-500/10 border-blue-500/40 text-blue-300 hover:border-blue-400" },
              { id: "grok", label: "Grok", accent: "from-zinc-500/20 to-zinc-600/10 border-zinc-400/50 text-zinc-200 hover:border-zinc-300" },
              { id: "perplexity", label: "Perplexity", accent: "from-sky-500/20 to-cyan-500/10 border-sky-500/40 text-sky-300 hover:border-sky-400" },
              { id: "notebooklm", label: "NotebookLM", accent: "from-violet-500/20 to-purple-500/10 border-violet-500/40 text-violet-300 hover:border-violet-400" },
            ];

            return (
            <div className="widget mb-6" data-testid="ai-report">
              <div className="widget-header flex items-center justify-between flex-wrap gap-2">
                <span className="widget-title">
                  <FileText className="w-4 h-4 inline mr-2 text-cyan-400" />
                  AI INTELLIGENCE REPORT
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="copy-ai-report-btn"
                    onClick={async () => {
                      const ok = await copyText(fullReportText);
                      if (ok) {
                        setCopiedReport(true);
                        toast.success("AI report copied — paste into any LLM");
                        setTimeout(() => setCopiedReport(false), 2500);
                      } else {
                        toast.error("Could not copy — select the text manually");
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded border border-cyan-500/40 bg-cyan-500/5 text-cyan-300 hover:bg-cyan-500/15 hover:border-cyan-400 transition-all"
                  >
                    {copiedReport ? (<><Check className="w-3.5 h-3.5" />Copied</>) : (<><Copy className="w-3.5 h-3.5" />Copy Report</>)}
                  </button>
                  <button
                    type="button"
                    data-testid="download-ai-report-btn"
                    onClick={() => {
                      const blob = new Blob([fullReportText], { type: "text/markdown;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      const safeHandle = (report.handle || "target").replace(/[^a-zA-Z0-9_-]/g, "_");
                      a.download = `global-scan-${safeHandle}-${(report.scan_id || "").slice(0, 8)}.md`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success("Report downloaded");
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded border border-zinc-600 bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700 hover:border-zinc-500 transition-all"
                  >
                    <Download className="w-3.5 h-3.5" />.md
                  </button>
                </div>
              </div>
              <div className="widget-content">
                {/* Send to LLM row */}
                <div
                  className="mb-4 flex items-center flex-wrap gap-2 pb-4 border-b border-zinc-800"
                  data-testid="send-to-llm-row"
                >
                  <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-zinc-500 mr-1">
                    <Send className="w-3 h-3" />
                    Send&nbsp;to&nbsp;LLM
                  </span>
                  {llmButtons.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`send-to-${b.id}-btn`}
                      onClick={() => sendTo(b.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded border bg-gradient-to-br ${b.accent} hover:scale-[1.03] active:scale-[0.98] transition-all`}
                      title={`Open ${b.label} in a new tab with the report ready to paste`}
                    >
                      {b.label}
                      <ExternalLink className="w-3 h-3 opacity-70" />
                    </button>
                  ))}
                </div>
                <div className="ai-output text-sm whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed text-zinc-300" data-testid="ai-report-text">
                  {report.ai_report}
                </div>
              </div>
            </div>
            );
          })()}
        </>
      )}

      {/* Compare Scans Button */}
      {report && pastReports.filter(r => r.status === "complete").length >= 2 && !scanDiff && (
        <div className="widget mb-6">
          <div className="widget-header">
            <span className="widget-title">
              <GitCompare className="w-4 h-4 inline mr-2 text-amber-400" />
              SCAN DIFF
            </span>
          </div>
          <div className="widget-content">
            <p className="text-xs text-zinc-500 mb-3">
              Compare your latest two scans to detect visibility changes over time.
            </p>
            <button
              onClick={runDiff}
              disabled={loadingDiff}
              data-testid="compare-scans-btn"
              className="px-6 py-2.5 text-xs font-bold bg-gradient-to-r from-amber-600 to-yellow-600 text-black hover:from-amber-500 hover:to-yellow-500 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {loadingDiff ? <><Loader2 className="w-4 h-4 animate-spin" /> COMPARING...</> :
                <><GitCompare className="w-4 h-4" /> COMPARE LATEST SCANS</>}
            </button>
          </div>
        </div>
      )}

      {/* Scan Diff Results */}
      {scanDiff && (
        <div data-testid="scan-diff-results">
          {/* Score Delta */}
          <div className="widget mb-6">
            <div className="widget-header">
              <span className="widget-title">
                <GitCompare className="w-4 h-4 inline mr-2 text-amber-400" />
                VISIBILITY CHANGE
              </span>
              <span className={`text-xs font-bold ${getDeltaColor(scanDiff.score_delta?.total || 0)}`}>
                {scanDiff.score_delta?.total > 0 ? "+" : ""}{scanDiff.score_delta?.total || 0} pts
              </span>
            </div>
            <div className="widget-content">
              <div className="flex items-center gap-6 mb-5">
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <p className="text-[10px] text-zinc-500">BEFORE</p>
                    <p className={`text-2xl font-bold ${getScoreColor(scanDiff.old_score || 0)}`}>{scanDiff.old_score}</p>
                  </div>
                  <span className="text-zinc-600 text-lg">&rarr;</span>
                  <div className="text-center">
                    <p className="text-[10px] text-zinc-500">AFTER</p>
                    <p className={`text-2xl font-bold ${getScoreColor(scanDiff.new_score || 0)}`}>{scanDiff.new_score}</p>
                  </div>
                  <div className={`text-center px-3 py-1 border ${getDeltaColor(scanDiff.score_delta?.total || 0)} border-current/30`}>
                    <p className="text-lg font-bold">
                      {getDeltaIcon(scanDiff.score_delta?.total || 0)} {scanDiff.score_delta?.total > 0 ? "+" : ""}{scanDiff.score_delta?.total || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sub-score deltas */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {["platform_coverage", "result_volume", "confidence_quality", "keyword_diversity", "regional_presence"].map(key => (
                  <div key={key} className="p-2 bg-zinc-900 border border-zinc-800 text-center">
                    <p className="text-[10px] text-zinc-500">{key.replace(/_/g, " ").toUpperCase()}</p>
                    <p className={`text-sm font-bold ${getDeltaColor(scanDiff.score_delta?.[key] || 0)}`}>
                      {getDeltaIcon(scanDiff.score_delta?.[key] || 0)} {scanDiff.score_delta?.[key] > 0 ? "+" : ""}{scanDiff.score_delta?.[key] || 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Platforms Gained / Lost */}
          {(scanDiff.platforms_gained?.length > 0 || scanDiff.platforms_lost?.length > 0) && (
            <div className="widget mb-6">
              <div className="widget-header">
                <span className="widget-title">PLATFORM CHANGES</span>
              </div>
              <div className="widget-content">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {scanDiff.platforms_gained?.length > 0 && (
                    <div className="border border-green-500/20 p-3">
                      <p className="text-xs text-green-400 font-semibold mb-2">PLATFORMS GAINED</p>
                      <div className="flex flex-wrap gap-1">
                        {scanDiff.platforms_gained.map(p => (
                          <span key={p} className={`text-xs px-2 py-0.5 border ${PLATFORM_COLORS[p] || "text-green-400 border-green-500/30"}`}>
                            {PLATFORM_ICONS[p] || p.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {scanDiff.platforms_lost?.length > 0 && (
                    <div className="border border-red-500/20 p-3">
                      <p className="text-xs text-red-400 font-semibold mb-2">PLATFORMS LOST</p>
                      <div className="flex flex-wrap gap-1">
                        {scanDiff.platforms_lost.map(p => (
                          <span key={p} className={`text-xs px-2 py-0.5 border border-red-500/30 text-red-400`}>
                            {PLATFORM_ICONS[p] || p.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Platform Result Changes */}
          <div className="widget mb-6">
            <div className="widget-header">
              <span className="widget-title">PER-PLATFORM CHANGES</span>
            </div>
            <div className="widget-content">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(scanDiff.platform_changes || {})
                  .sort((a, b) => Math.abs(b[1].delta) - Math.abs(a[1].delta))
                  .map(([plat, d]) => (
                  <div key={plat} className="p-2 bg-zinc-900 border border-zinc-800 flex items-center justify-between">
                    <span className={`text-[10px] font-bold ${PLATFORM_COLORS[plat]?.split(" ")[0] || "text-zinc-400"}`}>
                      {PLATFORM_ICONS[plat] || plat.toUpperCase()}
                    </span>
                    <span className="text-[10px] text-zinc-500">{d.old} &rarr; {d.new}</span>
                    <span className={`text-xs font-bold ${getDeltaColor(d.delta)}`}>
                      {d.delta > 0 ? "+" : ""}{d.delta}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Keyword Changes */}
          {(scanDiff.keywords_gained?.length > 0 || scanDiff.keywords_lost?.length > 0) && (
            <div className="widget mb-6">
              <div className="widget-header">
                <span className="widget-title">KEYWORD CHANGES</span>
              </div>
              <div className="widget-content">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {scanDiff.keywords_gained?.length > 0 && (
                    <div className="border border-green-500/20 p-3">
                      <p className="text-xs text-green-400 font-semibold mb-2">NEW KEYWORDS FOUND</p>
                      {scanDiff.keywords_gained.slice(0, 10).map(kw => (
                        <div key={kw} className="text-xs text-green-300 py-0.5">+ {kw}</div>
                      ))}
                    </div>
                  )}
                  {scanDiff.keywords_lost?.length > 0 && (
                    <div className="border border-red-500/20 p-3">
                      <p className="text-xs text-red-400 font-semibold mb-2">KEYWORDS LOST</p>
                      {scanDiff.keywords_lost.slice(0, 10).map(kw => (
                        <div key={kw} className="text-xs text-red-300 py-0.5">- {kw}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Restriction Changes */}
          {(scanDiff.restrictions_new?.length > 0 || scanDiff.restrictions_lifted?.length > 0) && (
            <div className="widget mb-6">
              <div className="widget-header">
                <span className="widget-title">
                  <Shield className="w-4 h-4 inline mr-2 text-red-400" />
                  RESTRICTION CHANGES
                </span>
              </div>
              <div className="widget-content space-y-2">
                {scanDiff.restrictions_new?.map((r, i) => (
                  <div key={`new-${i}`} className="flex items-center gap-2 p-2 bg-red-500/5 border border-red-500/20">
                    <span className="text-[9px] text-red-400 border border-red-500/30 px-1">NEW</span>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-red-300 truncate">{r.title || r.url}</a>
                  </div>
                ))}
                {scanDiff.restrictions_lifted?.map((r, i) => (
                  <div key={`lift-${i}`} className="flex items-center gap-2 p-2 bg-green-500/5 border border-green-500/20">
                    <span className="text-[9px] text-green-400 border border-green-500/30 px-1">LIFTED</span>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-green-300 truncate">{r.title || r.url}</a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regional Changes */}
          {scanDiff.regional_changes && Object.keys(scanDiff.regional_changes).length > 0 && (
            <div className="widget mb-6">
              <div className="widget-header">
                <span className="widget-title">
                  <MapPin className="w-4 h-4 inline mr-2 text-purple-400" />
                  REGIONAL CHANGES
                </span>
              </div>
              <div className="widget-content">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(scanDiff.regional_changes).map(([code, d]) => (
                    <div key={code} className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                      <p className="text-xs text-zinc-500 mb-1">{d.label || code}</p>
                      <p className="text-xs text-zinc-400">{d.old} &rarr; {d.new}</p>
                      <p className={`text-sm font-bold ${getDeltaColor(d.delta)}`}>
                        {getDeltaIcon(d.delta)} {d.delta > 0 ? "+" : ""}{d.delta}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* AI Diff Analysis */}
          {scanDiff.ai_analysis && (
            <div className="widget mb-6" data-testid="diff-ai-analysis">
              <div className="widget-header">
                <span className="widget-title">
                  <FileText className="w-4 h-4 inline mr-2 text-amber-400" />
                  AI CHANGE ANALYSIS
                </span>
              </div>
              <div className="widget-content">
                <div className="ai-output text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto leading-relaxed text-zinc-300">
                  {scanDiff.ai_analysis}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Past Reports */}
      {pastReports.length > 0 && !scanning && (
        <div className="widget mb-6">
          <div className="widget-header">
            <span className="widget-title">
              <RefreshCw className="w-4 h-4 inline mr-2 text-zinc-400" />
              PREVIOUS SCANS
            </span>
          </div>
          <div className="widget-content space-y-2">
            {pastReports.map((r, i) => (
              <button
                key={r.scan_id || i}
                onClick={() => r.status === "complete" && loadReport(r.channel_id || selectedChannel?.id)}
                className="w-full flex items-center justify-between p-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-colors text-left"
                data-testid={`past-report-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold ${r.status === "complete" ? "text-green-400" : "text-yellow-400"}`}>
                    {r.status?.toUpperCase() || "—"}
                  </span>
                  <span className="text-xs text-zinc-400">
                    {r.started_at ? new Date(r.started_at).toLocaleString() : "—"}
                  </span>
                </div>
                {r.visibility_score?.total != null && (
                  <span className={`text-sm font-bold ${getScoreColor(r.visibility_score.total)}`}>
                    {r.visibility_score.total}/100
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
